import React, { useCallback, useRef } from 'react'
import ModButton from '../ui/ModButton/ModButton'
import { useLang } from '../lib/useLang'

// Parse CSV string to get headers and row count
function detectDelimiter(headerLine) {
  const candidates = [',', ';', '\t', '|']
  let best = ','
  let maxCount = 0
  for (const d of candidates) {
    const count = headerLine.split(d).length
    if (count > maxCount) { maxCount = count; best = d }
  }
  return best
}

function parseCSVInfo(csvString) {
  const lines = csvString.trim().split('\n')
  if (lines.length === 0) return { headers: [], rowCount: 0 }
  const delimiter = detectDelimiter(lines[0])
  const headers = lines[0].split(delimiter).map(h => h.trim())
  return { headers, rowCount: lines.length - 1 }
}

// Convert predictions array to CSV with original data
function createResultCSV(originalCSV, predictions) {
  const lines = originalCSV.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n')
  if (lines.length === 0) return ''

  const header = lines[0] + ',prediction'
  const dataLines = lines.slice(1)

  const resultLines = [header]
  for (let i = 0; i < dataLines.length; i++) {
    const pred = predictions[i] !== undefined ? predictions[i] : ''
    resultLines.push(dataLines[i] + ',' + pred)
  }

  return resultLines.join('\n')
}

// Download string as file
function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export default function LabelDataView({
  onBack, workflowId, files, setFiles, result, setResult,
  status, setStatus, error, setError, progress, setProgress,
  workflowInfo, setWorkflowInfo,
}) {
  const { t } = useLang()
  const fileInputRef = useRef(null)

  const wfId = workflowId || ''

  // Handle file selection
  const handleFileSelect = useCallback(async (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    setStatus('loading')
    setError(null)

    try {
      const loadedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          const content = await file.text()
          const { headers, rowCount } = parseCSVInfo(content)
          return {
            name: file.name,
            content,
            headers,
            rowCount,
          }
        })
      )
      setFiles(loadedFiles)
      setStatus('idle')
    } catch (err) {
      setError(t('labelData.failedReadFiles') + err.message)
      setStatus('error')
    }
  }, [])

  // Handle drag and drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault()
    e.stopPropagation()

    const droppedFiles = Array.from(e.dataTransfer?.files || []).filter(f =>
      f.name.endsWith('.csv')
    )
    if (droppedFiles.length === 0) return

    setStatus('loading')
    setError(null)

    try {
      const loadedFiles = await Promise.all(
        droppedFiles.map(async (file) => {
          const content = await file.text()
          const { headers, rowCount } = parseCSVInfo(content)
          return {
            name: file.name,
            content,
            headers,
            rowCount,
          }
        })
      )
      setFiles(loadedFiles)
      setStatus('idle')
    } catch (err) {
      setError(t('labelData.failedReadFiles') + err.message)
      setStatus('error')
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Remove a file
  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Clear all
  const clearAll = useCallback(() => {
    setFiles([])
    setResult(null)
    setError(null)
    setStatus('idle')
    setProgress('')
    setWorkflowInfo(null)
  }, [])

  // Main prediction flow
  const runPrediction = useCallback(async () => {
    if (!wfId) {
      setError(t('labelData.noWorkflowId'))
      return
    }
    if (files.length === 0) {
      setError(t('labelData.uploadAtLeastOne'))
      return
    }

    setStatus('initializing')
    setError(null)
    setProgress(t('labelData.checkingModel'))

    try {
      // Step 1: Get workflow info
      const infoRes = await fetch(`/api/inference/info?workflow_id=${encodeURIComponent(wfId)}`)
      const info = await infoRes.json()
      if (info.error) throw new Error(info.error)
      setWorkflowInfo(info)

      // Step 2: Check if multi-table and we have the right files
      const isMultiTable = info.supports_multi_table && info.required_tables?.length > 0
      if (isMultiTable && files.length < info.required_tables.length) {
        throw new Error(
          t('labelData.requiresTables', { count: info.required_tables.length, tables: info.required_tables.join(', ') })
        )
      }

      // Step 3: Check status / init if needed
      setProgress(t('labelData.initContainer'))
      const statusRes = await fetch(`/api/inference/status?workflow_id=${encodeURIComponent(wfId)}`)
      const statusData = await statusRes.json()

      if (statusData.status !== 'READY') {
        // Initialize
        const initRes = await fetch('/api/inference/init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflow_id: wfId }),
        })
        const initData = await initRes.json()
        if (initData.error) throw new Error(initData.error)

        // Poll for ready status
        setProgress(t('labelData.waitingReady'))
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 2000))
          const pollRes = await fetch(`/api/inference/status?workflow_id=${encodeURIComponent(wfId)}`)
          const pollData = await pollRes.json()
          if (pollData.status === 'READY') break
          if (pollData.status === 'FAILED') throw new Error(t('labelData.modelInitFailed'))
          setProgress(t('labelData.waitingModelAttempt', { attempt: i + 1 }))
        }
      }

      // Step 4: Send prediction request
      setStatus('predicting')
      setProgress(t('labelData.runningPredictions'))

      let payload = { workflow_id: wfId, apply_transform: true }

      if (isMultiTable && files.length > 1) {
        // Multi-table mode: build tables dict
        const tables = {}
        for (const file of files) {
          // Use filename without extension as table name
          const tableName = file.name.replace(/\.csv$/i, '')
          tables[tableName] = file.content
        }
        payload.tables = tables
      } else {
        // Single-table mode
        payload.csv = files[0].content
      }

      const predictRes = await fetch('/api/inference/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const predictData = await predictRes.json()

      if (predictData.error) throw new Error(predictData.error)
      if (predictData.status === 'starting' || predictData.status === 'restarting') {
        throw new Error(t('labelData.modelStillStarting'))
      }

      // Step 5: Process results
      setProgress(t('labelData.processingResults'))
      const predictions = predictData.result?.result || predictData.result || []

      // Detect TS forecasts: array of objects with "mean" key
      const isTS = Array.isArray(predictions) && predictions.length > 0
        && typeof predictions[0] === 'object' && predictions[0] !== null && 'mean' in predictions[0]

      let resultCSV
      let rowCount
      if (isTS) {
        // TS: forecasts are separate rows (different timestamps from input).
        // Build a standalone CSV from the forecast dicts.
        const forecastKeys = Object.keys(predictions[0])
        const header = forecastKeys.join(',')
        const rows = predictions.map(p =>
          forecastKeys.map(k => {
            const v = p[k]
            return v === null || v === undefined ? '' : String(v)
          }).join(',')
        )
        resultCSV = [header, ...rows].join('\n')
        rowCount = predictions.length
      } else if (Array.isArray(predictions) && predictions.length > 0 && Array.isArray(predictions[0])) {
        // Multiclass: predictions are arrays of probabilities per class
        // Append proba_0, proba_1, ... columns to original CSV
        const nClasses = predictions[0].length
        const lines = files[0].content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n')
        const header = lines[0] + ',' + Array.from({length: nClasses}, (_, i) => `proba_${i}`).join(',')
        const dataLines = lines.slice(1)
        const resultLines = [header]
        for (let i = 0; i < dataLines.length; i++) {
          const probas = i < predictions.length ? predictions[i].join(',') : Array(nClasses).fill('').join(',')
          resultLines.push(dataLines[i] + ',' + probas)
        }
        resultCSV = resultLines.join('\n')
        rowCount = predictions.length
      } else {
        // Tabular scalar: one prediction per input row (regression / binary)
        const flatPredictions = Array.isArray(predictions)
          ? predictions.map(p => {
              if (typeof p !== 'object' || p === null) return p
              // Binary may return {proba_0: 0.437} or {prediction: 0.437}
              if (p.prediction !== undefined) return p.prediction
              if (p.proba_0 !== undefined) return p.proba_0
              // Fallback: first numeric value in the dict
              const vals = Object.values(p)
              return vals.length > 0 ? vals[0] : p
            })
          : []
        resultCSV = createResultCSV(files[0].content, flatPredictions)
        rowCount = flatPredictions.length
      }

      setResult({
        csv: resultCSV,
        predictions,
        rowCount,
      })
      setStatus('done')
      setProgress('')
    } catch (err) {
      setError(err.message)
      setStatus('error')
      setProgress('')
    }
  }, [wfId, files])

  // Download result
  const handleDownload = useCallback(() => {
    if (!result?.csv) return
    const filename = `predictions_${new Date().toISOString().slice(0, 10)}.csv`
    downloadCSV(result.csv, filename)
  }, [result])

  const isProcessing = status === 'initializing' || status === 'predicting' || status === 'loading'

  return (
    <div className='flex h-full min-w-0 flex-col'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-ink/10 px-6 py-4'>
        <div>
          <div className='text-lg font-semibold text-ink'>{t('labelData.title')}</div>
          {wfId && (
            <div className='mt-1 text-xs text-ink'>
              {t('labelData.workflowLabel')} <span className='font-mono text-ink'>{wfId}</span>
            </div>
          )}
        </div>
        <ModButton severity='secondary' onClick={onBack}>
          {t('labelData.back')}
        </ModButton>
      </div>

      {/* Content */}
      <div className='flex-1 space-y-6 overflow-auto p-6'>
        {/* Info banner */}
        <div className='rounded-lg border border-accent bg-accent/15 p-4'>
          <div className='flex items-start gap-3'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='mt-0.5 flex-shrink-0 text-accent'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='16' x2='12' y2='12' />
              <line x1='12' y1='8' x2='12.01' y2='8' />
            </svg>
            <div className='text-sm text-ink'>
              {t('labelData.infoBanner')}
              {workflowInfo?.supports_multi_table && (
                <span className='ml-1 text-accent'>
                  {t('labelData.multiTableSupport')}
                  {workflowInfo.required_tables?.length > 0 && (
                    <> ({t('labelData.required', { tables: workflowInfo.required_tables.join(', ') })})</>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* File Upload Area */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            isProcessing
              ? 'pointer-events-none border-ink/10 bg-ink/[0.03]'
              : 'border-ink/10 bg-ink/[0.03] hover:border-accent hover:bg-accent/15'
          }`}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv'
            multiple
            onChange={handleFileSelect}
            className='hidden'
            disabled={isProcessing}
          />
          <svg width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='mx-auto mb-3 text-ink'>
            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
            <polyline points='17,8 12,3 7,8' />
            <line x1='12' y1='3' x2='12' y2='15' />
          </svg>
          <div className='text-sm text-ink'>
            {isProcessing ? t('labelData.processing') : t('labelData.dropCsv')}
          </div>
          <div className='mt-1 text-xs text-ink'>
            {t('labelData.multiTableHint')}
          </div>
        </div>

        {/* Uploaded Files List */}
        {files.length > 0 && (
          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <div className='text-sm font-medium text-ink'>{t('labelData.uploadedFiles')}</div>
              <button
                onClick={clearAll}
                className='text-xs text-ink hover:text-ink'
                disabled={isProcessing}
              >
                {t('labelData.clearAll')}
              </button>
            </div>
            <div className='space-y-2'>
              {files.map((file, i) => (
                <div
                  key={i}
                  className='flex items-center justify-between rounded-lg border border-ink/10 bg-ink/[0.03] px-4 py-3'
                >
                  <div className='flex items-center gap-3'>
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                      <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' />
                      <polyline points='14,2 14,8 20,8' />
                    </svg>
                    <div>
                      <div className='text-sm text-ink'>{file.name}</div>
                      <div className='text-xs text-ink'>
                        {t('labelData.rowsColumns', { rows: file.rowCount, columns: file.headers.length })}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(i)}
                    className='text-ink hover:text-red-600'
                    disabled={isProcessing}
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                      <line x1='18' y1='6' x2='6' y2='18' />
                      <line x1='6' y1='6' x2='18' y2='18' />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className='rounded-lg border border-red-500/30 bg-red-500/10 p-4'>
            <div className='flex items-start gap-3'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='mt-0.5 flex-shrink-0 text-red-600'>
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
              <div className='text-sm text-red-500'>{error}</div>
            </div>
          </div>
        )}

        {/* Progress */}
        {isProcessing && progress && (
          <div className='rounded-lg border border-accent bg-accent/15 p-4'>
            <div className='flex items-center gap-3'>
              <svg className='h-5 w-5 animate-spin text-accent' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
              </svg>
              <div className='text-sm text-accent'>{progress}</div>
            </div>
          </div>
        )}

        {/* Action Button */}
        {files.length > 0 && status !== 'done' && (
          <ModButton
            onClick={runPrediction}
            disabled={isProcessing || !wfId}
            className='w-full'
          >
            {isProcessing ? t('labelData.processing') : t('labelData.getPredictions')}
          </ModButton>
        )}

        {/* Results */}
        {status === 'done' && result && (
          <div className='space-y-4'>
            <div className='rounded-lg border border-accent bg-accent/15 p-4'>
              <div className='flex items-center gap-3'>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-accent'>
                  <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                  <polyline points='22,4 12,14.01 9,11.01' />
                </svg>
                <div className='text-sm text-accent'>
                  {t('labelData.predictionsComplete', { count: result.rowCount })}
                </div>
              </div>
            </div>

            <ModButton onClick={handleDownload} className='w-full'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='mr-2'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='7,10 12,15 17,10' />
                <line x1='12' y1='15' x2='12' y2='3' />
              </svg>
              {t('labelData.downloadResults')}
            </ModButton>

            <ModButton severity='secondary' onClick={clearAll} className='w-full'>
              {t('labelData.startNew')}
            </ModButton>
          </div>
        )}
      </div>
    </div>
  )
}
